import { oauthAuthorize } from '../api/endpoints';
import type { Inbox } from '../api/types';
import { isManagedInbox } from '../lib/channelMeta';
import { writePendingMarker } from './oauthReturn';

/**
 * Fire the consent leg: mint the authorize URL, park the pending marker, leave.
 * Everything after this line happens on the provider's site and the CRM
 * callback — the console only sees the return redirect.
 */
async function leaveFor(inboxId: number, slug: string, provider?: 'google' | 'microsoft') {
  const { url } = await oauthAuthorize(inboxId, {
    ...(provider ? { provider } : {}),
    returnTo: window.location.href,
  });
  writePendingMarker({ inboxId, slug });
  window.location.assign(url);
}

/** Connect flow: the inbox was just created for `slug`. */
export async function launchConnectOauth(
  inboxId: number,
  slug: 'google' | 'microsoft' | 'ringcentral',
): Promise<void> {
  await leaveFor(inboxId, slug, slug === 'ringcentral' ? undefined : slug);
}

/** Reconnect on an existing OAuth-backed inbox (guard with reconnectViaOauth). */
export async function launchReconnect(inbox: Inbox): Promise<void> {
  if (inbox.channelType === 'email' && inbox.channel.provider) {
    await leaveFor(inbox.id, inbox.channel.provider, inbox.channel.provider);
    return;
  }
  if (inbox.channelType === 'ringcentral') {
    await leaveFor(inbox.id, 'ringcentral');
    return;
  }
  // The callback finishes a REconnect itself (same account, fresh token) and
  // comes back plain, exactly like a mailbox — only a first connect relays a grant.
  if (inbox.channelType === 'instagram' && isManagedInbox(inbox) && inbox.channel.source === 'instagram_login') {
    await leaveFor(inbox.id, 'instagram');
    return;
  }
  throw new Error('This channel reconnects by updating its credentials instead.');
}
